import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useListingRules from "../../data/useListingRules";
import useCategories from "../../data/useCategories";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import PopOutBox from "../../components/shared/PopOutBox";
import { useForm } from "react-hook-form";
import { CreateUpdateListingRuleRequest, IListingRule } from "../../models/ListingRule";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Card from "../../components/shared/Card/Card";
import Modal from "../../layouts/Modal";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import { DateTime } from "luxon";
import SelectableTable from "../../components/shared/Tables/SelectableTable";

export default function ListingRules() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({
      name: "Automatic Listing Rules",
      desc: "Manage rules for categories to automatically list to eBay",
    });
  }, []);

  return (
    <div className="mt-4">
      <RulesTab />
    </div>
  );
}

function RulesTab() {
  const [selected, setSelected] = useState<IListingRule | undefined>(undefined);
  const { listingRules, create, update, remove, setCategories, ruleCategories } = useListingRules(selected?.id);
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Listing Rule");
  const { register, handleSubmit, reset, setValue, watch } = useForm<CreateUpdateListingRuleRequest>({
    defaultValues: { name: "", hasPartNumber: false, allowOnCar: false, maximumPrice: undefined, locationIds: [] },
  });
  const { warehouseLocations } = useWarehouseLocations();
  const [locationsModalOpen, setLocationsModalOpen] = useState(false);
  const [selectedLocationRows, setSelectedLocationRows] = useState<string[]>([]);
  const formLocationIds = watch("locationIds");

  // Categories state (mirror locations behavior)
  const { categories: allCategories } = useCategories();
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [selectedCategoryRows, setSelectedCategoryRows] = useState<string[]>([]);

  const now = DateTime.local();

  // Round up to the next half-hour mark
  const minutesToAdd = now.minute < 30 ? (30 - now.minute) : (60 - now.minute);
  const nextRun = now.plus({ minutes: minutesToAdd }).set({ second: 0, millisecond: 0 });

  useEffect(() => {
    if (selected) {
      reset({
        name: selected.name ?? "",
        hasPartNumber: selected.hasPartNumber,
        allowOnCar: selected.allowOnCar,
        maximumPrice: selected.maximumPrice ?? undefined,
        locationIds: selected.locations?.map((l) => l.warehouseLocationId).filter(Boolean) ?? [],
      });
    } else {
      reset({ name: "", hasPartNumber: false, allowOnCar: false, maximumPrice: undefined, locationIds: [] });
    }
  }, [selected]);

  const onSubmit = (data: CreateUpdateListingRuleRequest) => {

    if (!data.maximumPrice) {
      data.maximumPrice = parseFloat(data.maximumPrice?.toString() ?? "");
    }

    if (!data.name) {
      data.name = `${data.hasPartNumber ? "Part Number Required" : "No Part Number Required"} + ${data.maximumPrice ? `Max £${data.maximumPrice}` : "No Max Price"}`
    }

    if (selected) {
      update.mutate({ id: selected.id, req: data }, {
        ...saveHandlers,
        onSettled: () => reset(data)
      });
    } else {
      create.mutate(data, {
        ...saveHandlers,
        onSuccess() {
          saveHandlers.onSuccess();
          reset();
        }
      });
    }
  };

  // Locations filtering and selection are handled by SelectableTable

  const openLocationsModal = () => {
    setSelectedLocationRows([...(formLocationIds ?? []) as string[]]);
    setLocationsModalOpen(true);
  };

  const applyLocations = () => {
    setValue("locationIds", selectedLocationRows);
    setLocationsModalOpen(false);
  };

  // Categories filtering and selection are handled by SelectableTable

  const openCategoriesModal = () => {
    if (!selected?.id) return;
    const existing = ruleCategories.data?.data ?? [];
    setSelectedCategoryRows([...(existing as string[])]);
    setCategoriesModalOpen(true);
  };

  const applyCategories = () => {
    if (!selected?.id) return;
    setCategories.mutate({ id: selected.id, categoryIds: selectedCategoryRows }, saveHandlers);
    setCategoriesModalOpen(false);
  };

  const onDelete = (rule: IListingRule) => {
    remove.mutate(rule.id, deleteHandlers);
    setSelected(undefined);
  };

  if (listingRules.isLoading) return <LoadingWheel />;

  return (
    <>
      <Modal open={locationsModalOpen} setOpen={setLocationsModalOpen} width="sm:max-w-3xl">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-left sm:mt-0 sm:ml-2 sm:text-left w-full">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Select Warehouse Locations</h3>
            <SelectableTable
              options={(warehouseLocations.data?.data ?? []).map(l => ({ id: l.id, name: l.displayName ?? "" }))}
              isLoading={warehouseLocations.isLoading}
              selectedIds={selectedLocationRows}
              setSelectedIds={setSelectedLocationRows}
              placeholder="Search locations"
            />
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonInput label={"Apply Selected"} isSubmit={false} onClick={applyLocations} classes="sm:ml-3 sm:w-auto w-full px-4" />
          <ButtonInput label={"Cancel"} isSubmit={false} onClick={() => setLocationsModalOpen(false)} classes="mt-3 sm:mt-0 sm:w-auto w-full bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 px-4" />
        </div>
      </Modal>

      <Modal open={categoriesModalOpen} setOpen={setCategoriesModalOpen} width="sm:max-w-3xl">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-left sm:mt-0 sm:ml-2 sm:text-left w-full">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Select Categories</h3>
            <SelectableTable
              options={(allCategories.data?.data ?? []).map(c => ({ id: c.id, name: c.name }))}
              isLoading={allCategories.isLoading}
              selectedIds={selectedCategoryRows}
              setSelectedIds={setSelectedCategoryRows}
              placeholder="Search categories"
            />
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonInput label={"Apply Selected"} isSubmit={false} onClick={applyCategories} classes="sm:ml-3 sm:w-auto w-full px-4" />
          <ButtonInput label={"Cancel"} isSubmit={false} onClick={() => setCategoriesModalOpen(false)} classes="mt-3 sm:mt-0 sm:w-auto w-full bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 px-4" />
        </div>
      </Modal>

      <div className="mb-4 flex justify-between text-gray-600">
        <p>After selecting locations and categories, an automated job will list eligible parts to eBay every 30 minutes.</p>
        <p>Next Run: {nextRun.toFormat("HH:mm")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        <Card title="Rules">
          <div className="min-h-[25vh]">
            <button
              className={"w-full text-left px-3 py-2 hover:bg-gray-100"}
              onClick={() => setSelected(undefined)}
            >
              + Create New Rule
            </button>

            {listingRules.data?.data?.map((r) => (
              <button
                key={r.id}
                className={"w-full text-left px-3 py-2 hover:bg-gray-100" + (selected?.id === r.id ? " bg-blue-50" : "")}
                onClick={() => setSelected(r)}
              >
                {r.name}
              </button>
            ))}
          </div>
        </Card>

        <Card title="Rule Details" containerClassName="lg:col-span-2">
          <form className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>

            <TextInput label="Rule Name" register={register} registerName="name" cols="sm:col-span-2" />

            <div className="flex items-center gap-x-4">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                <span>Part Number Required</span>
                <PopOutBox classes="p-2 w-64">
                  <div className="text-xs text-gray-700">Only list parts that have a part number present.</div>
                </PopOutBox>
              </label>
              <input type="checkbox" {...register("hasPartNumber")} />
            </div>
            <div className="flex items-center gap-x-4">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                <span>Allow On The Car Parts</span>
                <PopOutBox classes="ml-1 p-2 w-64">
                  <div className="text-xs text-gray-700">Include parts that have a status of "On The Car".</div>
                </PopOutBox>
              </label>
              <input type="checkbox" className="ml-2" {...register("allowOnCar")} />
            </div>

            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                <span>Maximum Price</span>
                <PopOutBox classes="ml-1 p-2 w-64">
                  <div className="text-xs text-gray-700">Only list parts up to this sale price.</div>
                </PopOutBox>
              </label>
              <NumberInput label="" register={register} registerName="maximumPrice" step=".01" />
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-700">Warehouse Locations</div>
                  <div className="text-xs text-gray-500">{(formLocationIds?.length ?? 0)} selected</div>
                </div>
                <ButtonInput label={"Edit Locations"} isSubmit={false} onClick={openLocationsModal} classes="" />
              </div>
            </div>

            {selected && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Categories</div>
                    <div className="text-xs text-gray-500">{ruleCategories.data?.data?.length ?? 0} selected</div>
                  </div>
                  <ButtonInput label={"Edit Categories"} isSubmit={false} onClick={openCategoriesModal} classes="" />
                </div>
              </div>
            )}

            <div className="sm:col-span-2 flex items-center justify-between">
              <ButtonInput label={selected ? "Save" : "Create"} isSubmit={true} onClick={undefined} classes="" />

              {selected && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => onDelete(selected)} classes="bg-red-600 hover:bg-red-700" />
              )}
            </div>

            {selected && (((formLocationIds?.length ?? 0) === 0) || ((ruleCategories.isSuccess && (ruleCategories.data?.data?.length ?? 0) === 0))) && (
              <div className="sm:col-span-2 mt-3">
                <div className="p-3 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm">
                  With no locations/categories selected, no parts will be selected for this rule.
                </div>
              </div>
            )}

          </form>
        </Card>
      </div>
    </>
  );
}


